import { createElement, getUiRoot, setText } from './dom';
import { createIcon } from './icons';
import { setLabel } from './i18n';
import { isChord } from '../controls/keymap';
import { EMOTES, emoteAvailable, type EmoteName, type Gesture } from '../multiplayer/emotes';

interface EmoteMenuState {
  readonly available: boolean;
  readonly character: 'male' | 'female';
  readonly gesture: Gesture | null;
  readonly pending: boolean;
}

function elements() {
  const launcher = createElement('button', 'player-context emote-launcher', getUiRoot());
  launcher.type = 'button';
  launcher.hidden = true;
  setLabel(launcher, 'aria-label', 'Gestures (J)');
  setLabel(launcher, 'title', 'Gestures (J)');
  const icon = createIcon('gesture', 20);
  icon.setAttribute('aria-hidden', 'true');
  launcher.appendChild(icon);
  const dialog = createElement('dialog', 'player-context emote-menu', getUiRoot());
  dialog.setAttribute('aria-labelledby', 'emote-title');
  const heading = createElement('div', 'emote-heading', dialog);
  const title = createElement('h2', '', heading);
  title.id = 'emote-title';
  setText(title, 'Gestures');
  const close = createElement('button', 'emote-close', heading);
  close.type = 'button';
  setLabel(close, 'aria-label', 'Close');
  setLabel(close, 'title', 'Close');
  close.appendChild(createIcon('close', 16));
  const list = createElement('div', 'emote-choices', dialog);
  const buttons = (Object.keys(EMOTES) as EmoteName[]).map((name, index) => {
    const button = createElement('button', '', list);
    button.type = 'button';
    button.dataset.emote = name;
    const label = createElement('span', '', button);
    setText(label, EMOTES[name].label);
    const key = createElement('kbd', '', button);
    key.textContent = String(index + 1);
    return { name, button };
  });
  const status = createElement('p', 'emote-status', dialog);
  status.hidden = true;
  return { launcher, dialog, close, buttons, status };
}

class EmoteMenu {
  private readonly ui = elements();
  private error: string | null = null;

  constructor(
    private readonly state: () => EmoteMenuState,
    private readonly changed: (open: boolean) => void,
    private readonly perform: (name: EmoteName) => Promise<void>
  ) {
    const { ui } = this;
    ui.launcher.addEventListener('click', () => {
      this.show();
    });
    ui.close.addEventListener('click', () => {
      this.hide();
    });
    ui.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.hide();
    });
    ui.dialog.addEventListener('click', this.outside);
    for (const { name, button } of ui.buttons)
      button.addEventListener('click', () => {
        void this.choose(name);
      });
    document.addEventListener('keydown', this.key, true);
  }

  get isOpen(): boolean {
    return this.ui.dialog.open;
  }

  hide(): void {
    if (!this.ui.dialog.open) return;
    this.ui.dialog.close();
    this.changed(false);
  }

  update(): void {
    const { ui } = this;
    const current = this.state();
    ui.launcher.hidden = !current.available;
    if (!current.available) this.hide();
    for (const { name, button } of ui.buttons) {
      const hat = emoteAvailable(name, current.character);
      button.disabled = !hat || current.pending || Boolean(current.gesture);
      setLabel(
        button,
        'title',
        hat ? EMOTES[name].label : 'Your citizen wears a hood, not a removable hat.'
      );
    }
    const label =
      this.error ??
      (current.pending
        ? 'Waiting for the city…'
        : current.gesture
          ? 'Let the gesture finish.'
          : null);
    ui.status.hidden = label === null;
    if (label) setText(ui.status, label);
    if (this.error) ui.status.setAttribute('role', 'alert');
    else ui.status.removeAttribute('role');
  }

  dispose(): void {
    this.hide();
    document.removeEventListener('keydown', this.key, true);
    this.ui.dialog.remove();
    this.ui.launcher.remove();
  }

  private show(): void {
    const { ui } = this;
    if (!this.state().available || ui.dialog.open) return;
    this.error = null;
    if (document.pointerLockElement) document.exitPointerLock();
    ui.dialog.showModal();
    this.changed(true);
    this.update();
    (ui.buttons.find(({ button }) => !button.disabled)?.button ?? ui.close).focus();
  }

  private async choose(name: EmoteName): Promise<void> {
    this.error = null;
    try {
      const request = this.perform(name);
      this.update();
      await request;
      this.hide();
    } catch (reason) {
      this.error =
        reason instanceof Error ? reason.message : 'The city did not confirm your gesture.';
      this.update();
    }
  }

  private readonly key = (event: KeyboardEvent): void => {
    const { ui } = this;
    if (event.repeat || isChord(event)) return;
    if (ui.dialog.open) {
      if (event.code === 'Escape' || event.code === 'KeyJ') {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.hide();
      } else if (/^Digit[1-7]$/.test(event.code)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const choice = ui.buttons[Number(event.code.slice(5)) - 1];
        if (choice && !choice.button.disabled) void this.choose(choice.name);
      }
      return;
    }
    if (event.code !== 'KeyJ') return;
    if (
      event.target instanceof HTMLElement &&
      event.target.closest('input, textarea, select, dialog, [role="dialog"]')
    )
      return;
    if (this.state().available) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.show();
    }
  };
  private readonly outside = (event: MouseEvent): void => {
    const { ui } = this;
    if (event.target === ui.dialog) {
      const rect = ui.dialog.getBoundingClientRect();
      if (
        event.clientX < rect.left ||
        event.clientX > rect.right ||
        event.clientY < rect.top ||
        event.clientY > rect.bottom
      )
        this.hide();
    }
  };
}

export function createEmoteMenu(
  state: () => EmoteMenuState,
  changed: (open: boolean) => void,
  perform: (name: EmoteName) => Promise<void>
) {
  return new EmoteMenu(state, changed, perform);
}
